import { Link } from 'react-router-dom'
import React from 'react'
import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/joy'
import { ResponsiveTable } from '../../../components/common/ResponsiveTable'
import Grid from '@mui/joy/Grid'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts'
import { useSalesSources } from '../../../hooks/useSalesReports'
import type { DateRange } from '../../../types/salesReports'

interface LeadSourceROIProps {
  dateRange: DateRange
}

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount)
}

const COLORS = ['#0B6BCB', '#06A77D', '#F9A825', '#E91E63', '#9C27B0', '#00BCD4', '#FF5722', '#795548']

export default function LeadSourceROI({ dateRange }: LeadSourceROIProps) {
  const { data, isLoading, error } = useSalesSources({
    date_from: dateRange.from,
    date_to: dateRange.to,
  })

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Alert color="danger">
        Failed to load lead source data. Please try again.
      </Alert>
    )
  }

  if (!data) {
    return (
      <Alert color="warning">
        No data available for the selected period.
      </Alert>
    )
  }

  const { opportunity_sources, contact_sources, summary } = data

  // Filter sources with opportunities for visualization
  const sourcesWithData = opportunity_sources.filter(s => s.opportunities > 0)

  // Pie chart data for revenue by source
  const pieData = sourcesWithData
    .filter(s => s.revenue > 0)
    .map(s => ({
      name: s.source_display,
      value: s.revenue,
    }))

  return (
    <Box>
      <Typography level="title-lg" sx={{ mb: 3 }}>
        Lead Source Analysis
      </Typography>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid xs={6} sm={3}>
          <Card variant="soft" color="primary">
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography level="body-xs">Total Opportunities</Typography>
              <Typography level="h3">{summary.total_opportunities}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid xs={6} sm={3}>
          <Card variant="soft" color="success">
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography level="body-xs">Total Won</Typography>
              <Typography level="h3">{summary.total_won}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid xs={6} sm={3}>
          <Card variant="soft" color="warning">
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography level="body-xs">Total Revenue</Typography>
              <Typography level="h3">{formatCurrency(summary.total_revenue)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid xs={6} sm={3}>
          <Card variant="soft" color="neutral">
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography level="body-xs">Overall Conversion</Typography>
              <Typography level="h3">{summary.overall_conversion_rate}%</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Revenue by Source Chart */}
        <Grid xs={12} lg={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography level="title-md" sx={{ mb: 2 }}>
                Revenue by Source
              </Typography>
              {pieData.length > 0 ? (
                <ResponsiveContainer width="100%" height={350}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      labelLine={true}
                      label={({ name, value }) => `${name}: ${formatCurrency(value as number)}`}
                      outerRadius={120}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {pieData.map((_, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatCurrency(value as number)} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <Alert color="neutral">No revenue data to display</Alert>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Conversion by Source Chart */}
        <Grid xs={12} lg={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography level="title-md" sx={{ mb: 2 }}>
                Conversion Rate by Source
              </Typography>
              {sourcesWithData.length > 0 ? (
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart data={sourcesWithData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                    <YAxis dataKey="source_display" type="category" width={120} />
                    <Tooltip formatter={(value) => `${value}%`} />
                    <Bar dataKey="conversion_rate" fill="#06A77D" name="Conversion Rate" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Alert color="neutral">No conversion data to display</Alert>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Opportunity Sources Table */}
      <Card variant="outlined" sx={{ mb: 4 }}>
        <CardContent>
          <Typography level="title-md" sx={{ mb: 2 }}>
            Opportunity Sources
          </Typography>
          <ResponsiveTable<typeof opportunity_sources[number]>
            rows={opportunity_sources}
            getRowKey={(s) => s.source}
            columns={[
              {
                key: 'source',
                sortValue: (row) => row.source_display,
                header: 'Source',
                mobileVariant: 'header',
                render: (s) => <Typography level="title-sm">{s.source_display}</Typography>,
              },
              { key: 'opportunities', sortValue: (row) => row.opportunities, header: 'Opportunities', render: (s) => <Typography level="body-sm">{s.opportunities}</Typography> },
              { key: 'won', sortValue: (row) => row.won, header: 'Won', render: (s) => <Typography level="body-sm">{s.won}</Typography> },
              {
                key: 'conversion_rate',
                header: 'Conversion Rate',
                render: (s) => (
                  <Chip
                    size="sm"
                    variant="soft"
                    color={s.conversion_rate >= 50 ? 'success' : s.conversion_rate >= 25 ? 'warning' : 'neutral'}
                  >
                    {s.conversion_rate}%
                  </Chip>
                ),
              },
              { key: 'revenue', sortValue: (row) => Number(row.revenue), header: 'Revenue', render: (s) => <Typography level="body-sm">{formatCurrency(s.revenue)}</Typography> },
              { key: 'avg_deal_size', sortValue: (row) => Number(row.avg_deal_size ?? 0), header: 'Avg Deal Size', render: (s) => <Typography level="body-sm">{formatCurrency(s.avg_deal_size)}</Typography> },
            ]}
          />
        </CardContent>
      </Card>

      <Alert color="neutral"><Typography component={Link} to="/crm/sales-desk">Open Sales Desk → Source profitability for recorded acquisition spend and completed-job profit. Budget estimates below are not verified campaign charges.</Typography></Alert>
      {/* Contact Sources with ROI */}
      {contact_sources.length > 0 && (
        <Card variant="outlined">
          <CardContent>
            <Typography level="title-md" sx={{ mb: 2 }}>
              Custom Lead Source Cohorts
            </Typography>
            <ResponsiveTable<typeof contact_sources[number]>
              rows={contact_sources}
              getRowKey={(s) => s.id}
              columns={[
                {
                  key: 'name',
                  header: 'Source',
                  mobileVariant: 'header',
                  render: (s) => <Typography level="title-sm">{s.name}</Typography>,
                },
                {
                  key: 'category',
                  sortValue: (row) => row.category_display,
                  header: 'Category',
                  render: (s) => (
                    <Chip size="sm" variant="soft">
                      {s.category_display}
                    </Chip>
                  ),
                },
                { key: 'leads', sortValue: (row) => row.leads, header: 'Leads', render: (s) => <Typography level="body-sm">{s.leads}</Typography> },
                { key: 'customers', sortValue: (row) => row.customers, header: 'Customers', render: (s) => <Typography level="body-sm">{s.customers}</Typography> },
                {
                  key: 'conversion_rate',
                  header: 'Conversion',
                  render: (s) => (
                    <Chip
                      size="sm"
                      variant="soft"
                      color={s.conversion_rate >= 10 ? 'success' : s.conversion_rate >= 5 ? 'warning' : 'neutral'}
                    >
                      {s.conversion_rate}%
                    </Chip>
                  ),
                },
                { key: 'revenue', sortValue: (row) => Number(row.revenue), header: 'Revenue', render: (s) => <Typography level="body-sm">{formatCurrency(s.revenue)}</Typography> },
                { key: 'cost', header: 'Budget estimate', render: (s) => <Typography level="body-sm">{formatCurrency(s.cost)}</Typography> },
                { key: 'cost_per_lead', header: 'Estimated cost/lead', render: (s) => <Typography level="body-sm">{formatCurrency(s.cost_per_lead)}</Typography> },
                {
                  key: 'roi',
                  header: 'Job-cost profit',
                  render: (s) =>
                    s.roi !== null ? (
                      <Chip
                        size="sm"
                        variant="soft"
                        color={s.roi >= 100 ? 'success' : s.roi >= 0 ? 'warning' : 'danger'}
                      >
                        {s.roi}%
                      </Chip>
                    ) : (
                      <Typography component={Link} to="/crm/sales-desk" level="body-xs">
                        View Sales Desk
                      </Typography>
                    ),
                },
              ]}
            />
          </CardContent>
        </Card>
      )}
    </Box>
  )
}
